import React, { Suspense, useEffect, useMemo, useRef } from 'react';
import { Avatar, ButtonBase, CircularProgress, Container, Stack, Typography } from '@mui/material';
import MuiBox from '@mui/material/Box';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment, OrbitControls, useGLTF } from '@react-three/drei';
import { BufferAttribute, Box3, Color, Group, MathUtils, SRGBColorSpace, ACESFilmicToneMapping, Vector3 } from 'three';
import { SkeletonUtils } from 'three-stdlib';
import { LineChart } from '@mui/x-charts/LineChart';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from 'chart.js';

import Bottom from '../Bottom';
import {
  DigitalTwinData,
  DiseaseApiKey,
  DiseaseRiskDetail,
  resolveDiseaseBadge,
  useDigitalTwin,
} from './digitalTwinApi';
import { bodyModelPath, isMaleSex, useUserSex } from '../../utils/bodyModel';
import iconBrainstorm from '../../assets/icon/iconBrainstorm.png';
import iconHeart from '../../assets/icon/iconHeart.png';
import iconKidneys from '../../assets/icon/iconKidneys.png';
import iconLiver from '../../assets/icon/iconLiver.svg';
import iconPancreas from '../../assets/icon/iconPancreas.png';
import iconIntestine from '../../assets/icon/iconIntestineBlue.png';
import iconCholesterol from '../../assets/icon/iconCholesterol.png';
import iconHeartRate from '../../assets/icon/iconHeartRate.png';
import iconWeightScale from '../../assets/icon/iconWeightScale.png';
import iconBlood from '../../assets/icon/iconBlood.png';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip);

export const Box = MuiBox as any;

/**
 * 접속 계정 성별에 맞는 전신 모델 경로. 확정되기 전에는 null.
 *
 * 판정 우선순위:
 *   1) `permission/getProfile` 의 sex — 프로필 API 가 'M'/'F' 로 명시하는 정본이고,
 *      심리 홈 화면도 같은 값을 쓰므로 두 화면의 성별이 어긋나지 않는다.
 *   2) 실패하면 AP_009_002 의 `user.sex` — 이미 받아둔 값이라 추가 호출이 없다.
 *      스펙에 true/false 정의가 없어 실측으로 확정한 값이므로 1)보다 뒤에 둔다.
 *
 * 확정 전에 null 을 주는 이유: 모델이 13MB 라 먼저 여성 모델을 받아놓고 나중에
 * 남성으로 바꾸면 26MB 를 받게 되고 화면에서 모델이 한 번 바뀌어 보인다.
 * 두 경로 모두 판정에 실패하면 여성 모델로 진행한다 — 안 뜨는 것보다 낫다.
 */
export const useBodyModelPath = (): string | null => {
  const { male, resolved } = useUserSex();
  const { data: twin, loading } = useDigitalTwin();

  if (resolved && male !== null) return bodyModelPath(male);
  if (!resolved || loading) return null;
  return bodyModelPath(isMaleSex(twin?.user?.sex));
};

// 모델이 Draco 로 압축되어 있어 런타임에 디코더가 필요하다.
// drei 기본값은 Google CDN(gstatic.com)에서 받아오므로, 외부 접근이 막히면
// 모델이 아예 뜨지 않는다. public/draco 에 디코더를 두고 그 경로를 쓴다.
export const DRACO_PATH = '/draco/';

export type Expression = 'neutral' | 'sad';

export type DiseaseRisk = {
  /** AP_009_002 의 질환 키 — 화면 간에 어떤 질환인지 넘길 때 쓴다 */
  key?: DiseaseApiKey;
  label: string;
  icon: string;
  status: '나쁨' | '주의' | '양호';
  /** AP_009_002 의 질환 판단 원문 (등급·신뢰도·근거·결측·해석) */
  detail?: DiseaseRiskDetail;
  /** 서버 등급 문구를 3단 배지로 환산하지 못한 경우 */
  gradeUnrecognized?: boolean;
  /** 아래 두 필드는 목업 전용 — AP_009_002 응답에는 없다 */
  tone?: 'bad' | 'warning';
  value?: string;
};

export type VitalMetric = {
  label: string;
  value: string;
  /**
   * 생략하면 기본(파랑) 스타일. 정상/비정상 색 구분은 붙이지 않는다 —
   * 이 프로젝트의 정상범위 기준이 파일마다 서로 모순된 상태라
   * 여기서 새 기준을 정하면 같은 문제를 하나 더 만드는 셈이 된다.
   */
  tone?: 'blue' | 'red';
  /** 검진값이 없어 '미측정'으로 표시되는 칸 (AP_009_002 는 결측을 null 로 내려준다) */
  missing?: boolean;
};

/**
 * 질환 카드 표시 순서 · 짧은 라벨 · 아이콘.
 * key 는 AP_009_002 응답의 disease 키다. 등급·점수·근거는 모두 서버에서 온다.
 * label 은 4칸 그리드에 맞춘 표기 — 서버 정식명(diseaseName)은 상세 시트에서 쓴다.
 */
export const DISEASE_CARD_DEFS: { key: DiseaseApiKey; label: string; icon: string }[] = [
  { key: 'dyslipidemia', label: '이상지질', icon: iconCholesterol },
  { key: 'diabetes', label: '당뇨병', icon: iconHeartRate },
  { key: 'obesity', label: '비만', icon: iconWeightScale },
  { key: 'hypertension', label: '고혈압', icon: iconBlood },
];

/**
 * AP_009_002 판단 결과 → 질환 카드 4장.
 * data 가 없으면 detail 이 비어 있는 카드를 돌려주므로, 호출부는 detail 유무로
 * '조회 전'과 '조회 완료'를 구분할 수 있다.
 */
export const buildDiseaseCards = (data: DigitalTwinData | null | undefined): DiseaseRisk[] =>
  DISEASE_CARD_DEFS.map((def) => {
    const detail = data?.disease?.[def.key];
    const { badge, matched } = resolveDiseaseBadge(detail?.riskGrade);
    return {
      key: def.key,
      label: def.label,
      icon: def.icon,
      status: badge,
      detail,
      gradeUnrecognized: detail ? !matched : false,
    };
  });

// 검진 지표 목업은 제거했다 — 값은 AP_009_002 의 user.healthIndicators 에서 온다
// (digitalTwinApi.buildVitalMetrics).

// risk 값은 목업이다. 이 배열을 쓰는 OrganSummaryGrid 는 현재 어느 화면에도 연결돼
// 있지 않다 — 되살릴 때는 risk 를 AP_009_002 의 organ[한글명].riskGrade 에서 파생시켜야
// 한다 (digitalTwinApi.isOrganAtRisk). 그대로 쓰면 근거 없는 위험 표시가 된다.
export const ORGAN_ITEMS = [
  { label: '뇌', value: 'brain', icon: iconBrainstorm, risk: '나쁨' },
  { label: '심장', value: 'heart', icon: iconHeart, risk: '나쁨' },
  { label: '신장', value: 'kidneys', icon: iconKidneys, risk: '보통' },
  { label: '간', value: 'liver', icon: iconLiver, risk: '주의' },
  { label: '대장', value: 'intestine', icon: iconIntestine, risk: '보통' },
  { label: '췌장', value: 'pancreas', icon: iconPancreas, risk: '주의' },
];

const RISK_MARKERS = [
  { id: 'head', x: '50%', y: '15%', tone: 'red' },
  { id: 'chest', x: '56%', y: '34%', tone: 'white' },
  { id: 'abdomen', x: '52%', y: '43%', tone: 'white' },
  { id: 'waist', x: '44%', y: '50%', tone: 'red' },
  { id: 'hip', x: '56%', y: '52%', tone: 'white' },
];

type MorphRefList = { influences: number[]; index: number }[];

const Model = ({
  expression,
  obesityLevel = 0,
  modelPath,
}: {
  expression: Expression;
  obesityLevel?: number;
  modelPath: string;
}) => {
  const { scene } = useGLTF(modelPath, DRACO_PATH);
  const model = useMemo(() => SkeletonUtils.clone(scene) as Group, [scene]);
  const sadnessTargets = useRef<MorphRefList>([]);
  const obesityTargets = useRef<MorphRefList>([]);
  const obesityRef = useRef<number>(obesityLevel);
  obesityRef.current = obesityLevel;

  useEffect(() => {
    sadnessTargets.current = [];
    obesityTargets.current = [];

    model.traverse((child: any) => {
      if (!child.isMesh) return;

      child.castShadow = true;
      child.receiveShadow = true;

      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((mat: any) => {
        if ('envMapIntensity' in mat) mat.envMapIntensity = 0.3;
        mat.needsUpdate = true;
      });

      const dict = child.morphTargetDictionary;
      if (dict && child.morphTargetInfluences) {
        if (typeof dict['슬픔'] === 'number') {
          sadnessTargets.current.push({ influences: child.morphTargetInfluences, index: dict['슬픔'] });
        }
        // '비만' shape key (없을 경우 'obesity', '뚱뚱' 등 대체 키 시도)
        //
        // '옷_뚱뚱' 은 남성 모델 전용이다. man.glb 의 옷 메시(Cube.004)만
        // 이 이름을 쓰고 나머지 메시는 '뚱뚱' 이라, 이 이름을 빼면 남성 모델에서
        // 몸만 불어나고 옷은 그대로 남는다.
        const obesityKey =
          typeof dict['비만'] === 'number'
            ? '비만'
            : typeof dict['obesity'] === 'number'
            ? 'obesity'
            : typeof dict['뚱뚱'] === 'number'
            ? '뚱뚱'
            : typeof dict['옷_뚱뚱'] === 'number'
            ? '옷_뚱뚱'
            : null;
        if (obesityKey) {
          obesityTargets.current.push({
            influences: child.morphTargetInfluences,
            index: dict[obesityKey],
          });
        }
      }
    });

    const box = new Box3().setFromObject(model);
    const size = box.getSize(new Vector3());
    const center = box.getCenter(new Vector3());
    const scale = size.y ? 2.7 / size.y : 1;
    model.scale.setScalar(scale);

    // 모델 중심을 (0, 0, 0)에 정확히 배치
    const scaledBox = new Box3().setFromObject(model);
    const scaledCenter = scaledBox.getCenter(center);
    model.position.x -= scaledCenter.x;
    model.position.y -= scaledCenter.y;
    model.position.z -= scaledCenter.z;
  }, [model]);

  useFrame((_, delta) => {
    const sadTarget = expression === 'sad' ? 1 : 0;
    const obeseTarget = Math.max(0, Math.min(1, obesityRef.current));
    const lerpFactor = Math.min(1, delta * 4);

    sadnessTargets.current.forEach(({ influences, index }) => {
      influences[index] = MathUtils.lerp(influences[index] ?? 0, sadTarget, lerpFactor);
    });
    obesityTargets.current.forEach(({ influences, index }) => {
      influences[index] = MathUtils.lerp(influences[index] ?? 0, obeseTarget, lerpFactor);
    });
  });

  return <primitive object={model} />;
};

// 모듈 로드 시점에는 성별을 알 수 없으므로 preload 하지 않는다.
// (예전에는 여기서 woman.glb 13MB 를 무조건 받았다 — 남성 계정에서는 그게 낭비였다.)

export const DigitalTwinShell = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ bgcolor: '#ecf0f2', minHeight: '100vh' }}>
    <Container
      maxWidth={false}
      sx={{
        position: 'relative',
        maxWidth: '430px !important',
        minWidth: 320,
        minHeight: '100vh',
        mx: 'auto',
        p: '0 !important',
        pb: '90px !important',
        bgcolor: '#ecf0f2',
        overflow: 'hidden',
      }}
    >
      {children}
      <Disclaimer />
    </Container>
    <Bottom />
  </Box>
);

/* 하단 안내 문구 — 화면 1~5 공통 */
export const Disclaimer = () => (
  <Typography
    sx={{
      mx: 2,
      mt: '30px',
      mb: 1,
      color: '#9aa6b2',
      fontSize: 12,
      lineHeight: 1.6,
      textAlign: 'center',
      whiteSpace: 'pre-line',
    }}
  >
    {'본 서비스에서 제공하는 내용은 진단 데이터가 아니며 예시 정보입니다.\n정확한 진단 및 상담은 반드시 의료·건강 전문가와 상담을 통해 진행해 주시기 바랍니다.'}
  </Typography>
);

export const PageTitle = ({
  children,
  accent,
}: {
  children: React.ReactNode;
  accent?: React.ReactNode;
}) => (
  <Typography sx={{ mt: 2.8, color: '#333', fontSize: 18, fontWeight: 800, textAlign: 'center', lineHeight: 1.35 }}>
    {children}
    {accent && (
      <Box component="span" sx={{ color: '#ff7a6a', fontSize: 20 }}>
        {accent}
      </Box>
    )}
  </Typography>
);

/**
 * 전신 모델 뷰어. 접속 계정의 성별에 맞는 모델을 띄운다.
 *
 * 성별은 프롭으로 받지 않고 useBodyModelPath 로 직접 읽는다 — 이 뷰어를 쓰는
 * 화면(4·5·BodyStage)이 모두 같은 계정을 보여주므로 호출부마다 성별을
 * 넘기게 하면 한 곳만 빠뜨려도 다른 성별 모델이 뜬다.
 */
export const BodyModelViewer = ({
  expression = 'neutral',
  obesityLevel = 0,
  zoom = 1,
  rotateEl,
}: {
  expression?: Expression;
  obesityLevel?: number;
  /**
   * 모델이 보이는 크기 배율. 1 = 기존 크기, 1.3 = 30% 크게.
   *
   * 카메라 zoom 을 쓰는 이유: fov 나 카메라 거리를 바꾸면 원근 왜곡까지 달라져
   * 화면마다 인체 비율이 다르게 보인다. zoom 은 화각을 그대로 두고 배율만 바꾼다.
   * 기본값 1 이라 이 프롭을 넘기지 않는 화면(화면 5 등)은 동작이 바뀌지 않는다.
   */
  zoom?: number;
  /**
   * 회전 제스처를 받을 DOM 영역. 넘기면 캔버스 전체가 아니라 이 영역에서만 회전하고
   * (캔버스는 터치를 받지 않아 그 밖의 스와이프는 시트/페이지 스크롤로 흘러간다),
   * 안 넘기면 기존처럼 캔버스 전체에서 회전한다 — 다른 화면 동작은 바뀌지 않는다.
   */
  rotateEl?: HTMLElement | null;
}) => {
  const modelPath = useBodyModelPath();

  return (
    <Canvas
      shadows="soft"
      dpr={[1, 2]}
      resize={{ debounce: 0, scroll: false }}
      camera={{ position: [0, 0, 5.25], fov: 31, zoom }}
      // rotateEl 이 있으면 캔버스는 터치를 받지 않는다(스크롤은 통과, 회전은 그 영역에서만)
      style={rotateEl ? { pointerEvents: 'none' } : undefined}
      gl={{
        alpha: true,
        antialias: true,
        outputColorSpace: SRGBColorSpace,
        toneMapping: ACESFilmicToneMapping,
        toneMappingExposure: 1.02,
      }}
    >
      <ambientLight intensity={0.65} />
      <directionalLight position={[2, 5, 4]} intensity={1.1} />
      <directionalLight position={[-3, 3, 2]} intensity={0.25} />
      <Environment preset="apartment" />
      <Suspense fallback={null}>
        <group position={[0, 0, 0]}>
          {/* 성별 확정 전에는 로드하지 않는다 (useBodyModelPath 주석 참고) */}
          {modelPath && (
            <Model expression={expression} obesityLevel={obesityLevel} modelPath={modelPath} />
          )}
        </group>
      </Suspense>
      <OrbitControls
        target={[0, 0, 0]}
        // 회전 제스처를 받을 영역 — 지정하면 이 영역에서만 회전(그 밖은 스크롤)
        domElement={rotateEl ?? undefined}
        enablePan={false}
        enableZoom={false}
        enableDamping
        dampingFactor={0.08}
        minPolarAngle={Math.PI / 2}
        maxPolarAngle={Math.PI / 2}
      />
    </Canvas>
  );
};

/* =========================================================================
 *  장기 모델 뷰어 (화면 2 - 뇌/심장/신장/간/대장/췌장)
 * ========================================================================= */

// 장기 GLB 경로 매핑.
export const ORGAN_MODEL_MAP: Record<string, { path: string; label: string; temp?: boolean }> = {
  brain:     { path: '/model/Brain.glb',     label: '뇌' },
  heart:     { path: '/model/heart.glb',     label: '심장' },
  kidneys:   { path: '/model/kidneys.glb',   label: '신장' },
  liver:     { path: '/model/liver.glb',     label: '간' },
  intestine: { path: '/model/intestine.glb', label: '대장' },
  pancreas:  { path: '/model/pancreas.glb',  label: '췌장' },
};

// 장기별 건강 등급(grade 0~4) Color Ramp — Blender Color Ramp 색상 단계를 코드로 재현
//  각 배열은 grade 0(정상) → 4(악화) 순서의 색상 단계
export const ORGAN_GRADE_RAMP: Record<string, string[]> = {
  // 췌장: 연한 베이지/살구 → 노랑·주황 → 갈색·붉은
  pancreas:  ['#E8C9A0', '#E3C07E', '#D99E4E', '#C8703A', '#9E4A2E'],
  // 심장: 선명한 붉은색 → 약간 어두움 → 주황·암적색 → 진한 적색 → 어두운 적색·자주
  heart:     ['#E5392F', '#CE2E2A', '#B83A24', '#8C2420', '#5C1A2C'],
  // 신장: 진한 적갈색 → 갈색·회색 → 어두운 갈색
  kidneys:   ['#9C4B33', '#8E4730', '#7C5240', '#5E3528', '#4A3A33'],
  // 간: 적갈색 매끈 → 노란빛 → 노랑·주황 → 주황·갈색 → 탁한 갈색·붉은·노랑 혼합
  liver:     ['#8C3B2A', '#9C4F2A', '#B56E2E', '#A85B26', '#7C4820'],
  // 뇌: 연한 분홍빛 회색 → 회색·붉은기 → 어두운 자주·붉은
  brain:     ['#CBB4B2', '#B49C99', '#A78F80', '#876B66', '#5C4751'],
  // 대장: 연한 베이지/살색 → 노랑·주황 → 붉은·주황
  intestine: ['#E3C0A0', '#DDB37E', '#CE8F54', '#C2703E', '#A2502E'],
  // 폐: 연분홍 → 탁한 분홍·회색 → 어두운 회색
  lungs:     ['#E8B3B8', '#D7A0A4', '#C0989A', '#9E7A7C', '#7A5E60'],
};

// hex 배열을 Color 객체 배열로 캐싱
const _rampCache: Record<string, Color[]> = {};
const getRamp = (key?: string): Color[] | null => {
  if (!key || !ORGAN_GRADE_RAMP[key]) return null;
  if (!_rampCache[key]) _rampCache[key] = ORGAN_GRADE_RAMP[key].map((h) => new Color(h));
  return _rampCache[key];
};

// 마스크 장기에서 "마스크 없는(정상) 영역"의 고정 기준색. 없으면 ramp[0] 사용.
export const MASK_BASE_COLOR: Record<string, string> = {
  intestine: '#E8B79E',
};
const _maskBaseCache: Record<string, Color> = {};
const getMaskBase = (key: string | undefined, fallback: Color): Color => {
  if (!key || !MASK_BASE_COLOR[key]) return fallback;
  if (!_maskBaseCache[key]) _maskBaseCache[key] = new Color(MASK_BASE_COLOR[key]);
  return _maskBaseCache[key];
};

// 모델에 칠해진 정점색(COLOR_0)을 ramp로 덮지 않고 "원본 그대로" 보여줄 장기
const VERTEX_COLOR_AS_IS = new Set<string>([]);


// 마스크 영역 악화색 최대 강도 (1=100% 덮음). 낮출수록 연하고 경계가 자연스러움.
const MASK_INTENSITY = 0.6;

const _rampTmp = new Color();
// t(0~1)를 ramp 전체 구간(grade 0~4)에 매핑해 색상 보간
const sampleGradeRamp = (ramp: Color[], t: number): Color => {
  const segs = ramp.length - 1;
  const x = Math.max(0, Math.min(1, t)) * segs;
  const i = Math.min(segs - 1, Math.floor(x));
  return _rampTmp.copy(ramp[i]).lerp(ramp[i + 1], x - i);
};

const OrganModel = ({
  path,
  decay = 0,
  organKey,
  gradeOverride,
}: {
  path: string;
  decay?: number;
  organKey?: string;
  gradeOverride?: number; // 지정 시 grade를 이 값으로 고정 (custom property·decay 무시)
}) => {
  const { scene } = useGLTF(path, DRACO_PATH);
  const model = useMemo(() => SkeletonUtils.clone(scene) as Group, [scene]);
  // 균일 색상 재질 (마스크 없는 장기)
  const matsRef = useRef<{ mat: any; baseRough: number }[]>([]);
  // 마스크 기반 재질 (대장 등 — COLOR_1 정점 마스크 사용)
  const maskRef = useRef<{ colorAttr: any; colorArr: Float32Array; baseArr: Float32Array; mask: Float32Array; count: number; mat: any; baseRough: number }[]>([]);
  const shownDecayRef = useRef(0); // 현재 화면에 표시 중인 decay (부드러운 보간용)
  const lastMaskT = useRef(-1); // 마스크 색상 재계산 최적화용
  const decayRef = useRef(decay);
  decayRef.current = decay;
  const baseGradeRef = useRef(0); // 모델 Custom Property 'grade' — 시작 등급
  const ramp = getRamp(organKey); // 장기별 grade Color Ramp (없으면 색상 미적용)

  useEffect(() => {
    matsRef.current = [];
    maskRef.current = [];
    lastMaskT.current = -1;
    baseGradeRef.current = 0;
    // Custom Property 'grade' 읽기 + 마스크 존재 여부 감지
    let hasMask = false;
    model.traverse((child: any) => {
      const g = child?.userData?.grade;
      if (typeof g === 'number') baseGradeRef.current = g;
      const a = child?.geometry?.attributes;
      if (child?.isMesh && a && (a.color_1 || a.color_2)) hasMask = true;
    });
    model.traverse((child: any) => {
      if (!child.isMesh) return;
      child.castShadow = true;
      child.receiveShadow = true;
      // 재질을 인스턴스마다 복제 — 캐시된 원본 재질 오염 방지
      child.material = Array.isArray(child.material)
        ? child.material.map((m: any) => m.clone())
        : child.material.clone();
      const geo = child.geometry;
      // 마스크 = 마지막 색상 레이어. 보통 COLOR_1이지만, 베이스 색 레이어가 더 있으면 COLOR_2가 됨(심장).
      const maskAttr = geo?.attributes?.color_2 || geo?.attributes?.color_1;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((mat: any) => {
        if ('envMapIntensity' in mat) mat.envMapIntensity = 0.4;
        const baseRough = typeof mat.roughness === 'number' ? mat.roughness : 0.6;

        if (organKey && VERTEX_COLOR_AS_IS.has(organKey) && geo?.attributes?.color) {
          // ── 심장: 모델에 칠한 정점색(COLOR_0)을 ramp로 덮지 않고 원본 그대로 표시 ──
          mat.vertexColors = true;
          if (mat.color) mat.color.set(0xffffff); // 곱셈 영향 제거 → 칠한 색 그대로
          // matsRef/maskRef에 넣지 않음 → useFrame에서 색을 건드리지 않음
        } else if (ramp && maskAttr && geo.attributes.position) {
          // ── 마스크 장기: 마스크 영역에만 악화색, 정상 영역은 베이스색 유지 ──
          const count = geo.attributes.position.count;
          const mArr = maskAttr.array;
          const mDen = mArr instanceof Uint16Array ? 65535 : mArr instanceof Uint8Array ? 255 : 1;
          const mIs = maskAttr.itemSize;
          const mask = new Float32Array(count);
          let mSum = 0;
          for (let i = 0; i < count; i++) { const v = mArr[i * mIs] / mDen; mask[i] = v; mSum += v; }
          const mAvg = mSum / count;
          // 마스크가 전부 검정(≈0) → 병변 없음. 전부 흰색(≈1) → 마스크 미지정으로 보고 장기 전체 변화(1 유지)
          if (mAvg < 0.03) mask.fill(0);
          else if (mAvg > 0.92) mask.fill(1);

          // 정상(마스크 없는) 영역 기준색 — 한 가지 색으로 통일 (얼룩 방지)
          //  우선순위: MASK_BASE_COLOR 지정색 > 베이스 레이어의 대표(최다) 칠한 색 > grade0
          const b0 = ramp[0];
          let baseR = b0.r, baseG = b0.g, baseB = b0.b;
          if (organKey && MASK_BASE_COLOR[organKey]) {
            const c = getMaskBase(organKey, ramp[0]);
            baseR = c.r; baseG = c.g; baseB = c.b;
          } else {
            const baseAttr = maskAttr === geo.attributes.color_2 ? geo.attributes.color_1 : geo.attributes.color;
            if (baseAttr) {
              const bArr = baseAttr.array;
              const bDen = bArr instanceof Uint16Array ? 65535 : bArr instanceof Uint8Array ? 255 : 1;
              const bIs = baseAttr.itemSize;
              // 칠한(검정·흰색 제외) 색들의 최다 색을 대표 베이스로
              const cnt = new Map<string, { n: number; r: number; g: number; bl: number }>();
              for (let i = 0; i < count; i++) {
                const r = bArr[i * bIs] / bDen, g = bArr[i * bIs + 1] / bDen, bl = bArr[i * bIs + 2] / bDen;
                if (r + g + bl < 0.12) continue;      // 검정(안 칠함) 제외
                if (r > 0.96 && g > 0.96 && bl > 0.96) continue; // 흰색 제외
                const key = `${Math.round(r * 12)}_${Math.round(g * 12)}_${Math.round(bl * 12)}`;
                const e = cnt.get(key);
                if (e) { e.n++; } else cnt.set(key, { n: 1, r, g, bl });
              }
              // 최다 색을 고른다. 객체 변수 대신 숫자 변수로 받는 이유:
              // TS 는 콜백 안의 대입을 제어흐름에 반영하지 않아, `let best: T|null = null`
              // 를 forEach 로 채우면 이후 best 가 never 로 좁혀져 TS2339 가 난다.
              let bestN = 0;
              let bestR = 0, bestG = 0, bestBl = 0;
              cnt.forEach((e) => { if (e.n > bestN) { bestN = e.n; bestR = e.r; bestG = e.g; bestBl = e.bl; } });
              if (bestN > 0) { baseR = bestR; baseG = bestG; baseB = bestBl; }
            }
          }
          const baseArr = new Float32Array(count * 3);
          for (let i = 0; i < count; i++) { baseArr[i * 3] = baseR; baseArr[i * 3 + 1] = baseG; baseArr[i * 3 + 2] = baseB; }

          // 렌더용 color 버퍼 (정수형 원본 대신 float) — 초기값 = 베이스색
          const colorArr = new Float32Array(count * 3);
          colorArr.set(baseArr);
          geo.setAttribute('color', new BufferAttribute(colorArr, 3));
          mat.vertexColors = true;
          if (mat.color) mat.color.set(0xffffff); // 정점색 그대로 보이게 흰색
          maskRef.current.push({ colorAttr: geo.attributes.color, colorArr, baseArr, mask, count, mat, baseRough });
        } else if (mat.color && !hasMask) {
          // ── 균일 색상 장기 (마스크 없는 단일 장기) ──
          // 칠해진 정점색(COLOR_0)이 있으면 끔 → ramp 색과 곱해져 어두워지는 것 방지
          if (mat.vertexColors) mat.vertexColors = false;
          matsRef.current.push({ mat, baseRough });
        }
        // 마스크 장기의 마스크 없는 메시(예: 간의 부속 메시)는 원래 색 유지
        mat.needsUpdate = true;
      });
    });

    // 모델 크기 정규화 + 중심을 (0,0,0)에 배치
    const box = new Box3().setFromObject(model);
    const size = box.getSize(new Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = maxDim ? 2.4 / maxDim : 1;
    model.scale.setScalar(scale);

    const scaledBox = new Box3().setFromObject(model);
    const scaledCenter = scaledBox.getCenter(new Vector3());
    model.position.x -= scaledCenter.x;
    model.position.y -= scaledCenter.y;
    model.position.z -= scaledCenter.z;
  }, [model, ramp]);

  // 연도 드래그 → decay(0~1). 시작 등급은 모델 Custom Property 'grade'.
  useFrame((_, delta) => {
    if (!ramp) return; // 해당 장기에 grade ramp가 없으면 색상 미적용
    const maxGrade = ramp.length - 1; // 4
    let t: number;
    if (typeof gradeOverride === 'number') {
      // grade 고정 (화면 1 등) — custom property·연도 무시
      t = Math.max(0, Math.min(1, gradeOverride / maxGrade));
    } else {
      const target = Math.max(0, Math.min(1, decayRef.current));
      shownDecayRef.current = MathUtils.lerp(shownDecayRef.current, target, Math.min(1, delta * 4));
      const dDecay = shownDecayRef.current;
      const base = Math.max(0, Math.min(maxGrade, baseGradeRef.current));
      const grade = base + dDecay * (maxGrade - base); // 현재 등급(0~maxGrade)
      t = grade / maxGrade; // ramp 위치(0~1)
    }
    const rampColor = sampleGradeRamp(ramp, t); // 현재 등급의 악화색

    // 균일 색상 장기
    matsRef.current.forEach(({ mat, baseRough }) => {
      if (mat.color) mat.color.copy(rampColor);
      if (typeof mat.roughness === 'number') mat.roughness = MathUtils.lerp(baseRough, 1, t);
      if (typeof mat.metalness === 'number') mat.metalness = MathUtils.lerp(mat.metalness, 0, t);
    });

    // 마스크 장기: 정상 영역 = 베이스색(정점별) / 병변 영역 = 악화색, 마스크로 블렌딩
    if (maskRef.current.length && Math.abs(t - lastMaskT.current) > 0.002) {
      lastMaskT.current = t;
      const dr = rampColor.r, dg = rampColor.g, db = rampColor.b;
      maskRef.current.forEach(({ colorArr, baseArr, colorAttr, mask, count, mat, baseRough }) => {
        for (let i = 0; i < count; i++) {
          const m = mask[i] * MASK_INTENSITY; // 강도 완화 → 연하고 경계 자연스럽게
          const k = i * 3;
          colorArr[k] = baseArr[k] + (dr - baseArr[k]) * m;
          colorArr[k + 1] = baseArr[k + 1] + (dg - baseArr[k + 1]) * m;
          colorArr[k + 2] = baseArr[k + 2] + (db - baseArr[k + 2]) * m;
        }
        colorAttr.needsUpdate = true;
        if (typeof mat.roughness === 'number') mat.roughness = MathUtils.lerp(baseRough, 1, t);
        if (typeof mat.metalness === 'number') mat.metalness = MathUtils.lerp(mat.metalness, 0, t);
      });
    }
  });

  return <primitive object={model} />;
};

/**
 * 장기 3D 모델 뷰어. path 로 GLB 파일을 지정.
 * decay(0~1): 연도 진행에 따른 악화 정도(grade 0~4).
 * organKey: 장기 키 — 해당 장기의 Color Ramp로 색상·질감 적용.
 * 자동 회전 + 좌우 수동 회전 가능.
 */
export const OrganModelViewer = ({
  path,
  decay = 0,
  organKey,
  gradeOverride,
}: {
  path: string;
  decay?: number;
  organKey?: string;
  gradeOverride?: number;
}) => (
  <Canvas
    shadows="soft"
    dpr={[1, 2]}
    resize={{ debounce: 0, scroll: false }}
    camera={{ position: [0, 0, 4.6], fov: 35 }}
    style={{ filter: 'saturate(1.15)' }}
    gl={{
      alpha: true,
      antialias: true,
      outputColorSpace: SRGBColorSpace,
      toneMapping: ACESFilmicToneMapping,
      toneMappingExposure: 1.05,
    }}
  >
    {/* 사방(앞·뒤·좌·우) 동일 세기 조명 — 어느 면이든 균일하게 밝게 */}
    <ambientLight intensity={0.6} />
    <directionalLight position={[0, 3, 5]} intensity={0.8} />   {/* 앞 */}
    <directionalLight position={[0, 3, -5]} intensity={0.8} />  {/* 뒤 */}
    <directionalLight position={[5, 3, 0]} intensity={0.8} />   {/* 우 */}
    <directionalLight position={[-5, 3, 0]} intensity={0.8} />  {/* 좌 */}
    <Environment preset="apartment" />
    <Suspense fallback={null}>
      <group position={[0, 0, 0]}>
        <OrganModel path={path} decay={decay} organKey={organKey} gradeOverride={gradeOverride} />
      </group>
    </Suspense>
    <OrbitControls
      target={[0, 0, 0]}
      enablePan={false}
      enableZoom={false}
      enableDamping
      dampingFactor={0.08}
      autoRotate
      autoRotateSpeed={1.6}
      minPolarAngle={Math.PI / 2}
      maxPolarAngle={Math.PI / 2}
    />
  </Canvas>
);

export const BodyStage = ({
  height = 360,
  expression = 'neutral',
  showMarkers = true,
}: {
  height?: number;
  expression?: Expression;
  showMarkers?: boolean;
}) => (
  <Box sx={{ position: 'relative', height, mx: 2, mt: 2 }}>
    <Box sx={{ position: 'absolute', inset: '-2px -34px 0 26px', zIndex: 1 }}>
      <Suspense
        fallback={
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: '#8aa2ad' }} />
          </Box>
        }
      >
        <BodyModelViewer expression={expression} />
      </Suspense>
    </Box>

    {showMarkers && (
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}>
        {RISK_MARKERS.map((marker) => (
          <Box
            key={marker.id}
            sx={{
              position: 'absolute',
              left: marker.x,
              top: marker.y,
              width: marker.tone === 'red' ? 22 : 18,
              height: marker.tone === 'red' ? 22 : 18,
              borderRadius: '50%',
              bgcolor: marker.tone === 'red' ? '#ed5454' : 'rgba(255,255,255,.86)',
              border: marker.tone === 'red' ? '6px solid rgba(255,255,255,.88)' : 'none',
              boxShadow: marker.tone === 'red' ? '0 2px 10px rgba(237,84,84,.28)' : '0 4px 10px rgba(177,190,198,.35)',
              transform: 'translate(-50%, -50%)',
            }}
          />
        ))}
      </Box>
    )}
  </Box>
);

// 질환 상태별 색상 (화면 1과 동일)
const DISEASE_STATUS_COLOR: Record<string, string> = {
  '나쁨': '#ed5454',
  '주의': '#ff8000',
  '양호': '#38c172',
};

// 질환 카드 — 화면 1(Modeling)의 DiseaseSummary와 동일한 디자인
export const DiseaseCards = ({
  diseases,
  onSelect,
}: {
  /** AP_009_002 판단 결과에서 만든 카드. 호출부가 조회 완료 후에만 렌더한다. */
  diseases: DiseaseRisk[];
  onSelect?: (disease: DiseaseRisk) => void;
}) => (
  <Box sx={{ mx: 2, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(68px, 1fr))', gap: '8px' }}>
    {diseases.map((disease) => (
      <ButtonBase
        key={disease.label}
        // 서버 판단 결과가 없으면 상세 시트를 열 게 없다 (화면 1의 DiseaseSummary 와 같은 규칙)
        disabled={!disease.detail}
        onClick={() => onSelect?.(disease)}
        sx={{
          height: 101,
          minWidth: 0,
          bgcolor: '#fff',
          borderRadius: '14px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '8px 7px 5px',
          boxSizing: 'border-box',
        }}
      >
        <Typography sx={{ fontSize: 15, color: '#333', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
          {disease.label}
        </Typography>
        <Box
          sx={{
            width: '100%',
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible',
          }}
        >
          <Box
            component="img"
            src={disease.icon}
            alt={disease.label}
            sx={{ width: 34, height: 34, objectFit: 'contain', display: 'block' }}
          />
        </Box>
        {/* 서버 판단 결과가 있을 때만 색 배지 — 조회 전에는 회색 '—' (화면 1과 동일 규칙) */}
        <Box
          title={disease.detail ? disease.detail.riskGrade : '건강 데이터 미조회'}
          sx={{
            width: '100%',
            maxWidth: 68,
            height: 21,
            borderRadius: '50px',
            bgcolor: disease.detail ? DISEASE_STATUS_COLOR[disease.status] ?? '#ff8000' : '#dfe5ec',
            color: disease.detail ? '#fff' : '#98a5b6',
            fontSize: 13,
            fontWeight: 800,
            lineHeight: '21px',
          }}
        >
          {disease.detail ? disease.status : '—'}
        </Box>
      </ButtonBase>
    ))}
  </Box>
);

// buildVitalMetrics 가 4칸을 주므로 2열(2×2)로 배치한다 — 3열이면 둘째 줄에 한 칸만 남는다.
export const MetricGrid = ({ metrics }: { metrics: VitalMetric[] }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '13px 8px' }}>
    {metrics.map((metric) => (
      <Box
        key={metric.label}
        sx={{
          height: 64,
          borderRadius: '14px',
          border: '1px solid rgba(255,255,255,.5)',
          bgcolor: metric.missing ? '#eaeef2' : metric.tone === 'red' ? '#f5dcdd' : '#e8ecf1',
          boxShadow: metric.missing
            ? 'none'
            : metric.tone === 'red'
            ? '4px 4px 4px #c5d3da'
            : 'inset 4px 4px 4px #c5d3da',
          p: '10px 13px',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            color: metric.missing ? '#98a5b6' : '#333',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          {metric.label}
        </Typography>
        <Typography
          sx={{
            mt: 2,
            color: metric.missing ? '#a8b3c0' : metric.tone === 'red' ? '#e37579' : '#54a4cb',
            fontSize: metric.missing ? 12 : 13,
            fontWeight: metric.missing ? 500 : 800,
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          {metric.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

export const SectionCard = ({ children, sx }: { children: React.ReactNode; sx?: any }) => (
  <Box
    sx={{
      mx: 2,
      mt: 2,
      bgcolor: '#fff',
      borderRadius: '14px',
      border: '1px solid rgba(255,255,255,.6)',
      boxShadow: '4px 4px 8px rgba(197,211,218,.65)',
      p: 2,
      boxSizing: 'border-box',
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const StatTriplet = ({
  items,
}: {
  items: { label: string; value: string; tone?: 'red' | 'blue' | 'orange' }[];
}) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
    {items.map((item) => (
      <Box key={item.label} sx={{ bgcolor: '#f3f6fa', borderRadius: '10px', p: 1.2, minHeight: 72, boxSizing: 'border-box' }}>
        <Typography sx={{ color: '#555', fontSize: 12, lineHeight: 1.25 }}>{item.label}</Typography>
        <Typography
          sx={{
            mt: 1.1,
            color: item.tone === 'red' ? '#e16569' : item.tone === 'orange' ? '#ff8000' : '#54a4cb',
            fontSize: 15,
            fontWeight: 800,
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          {item.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

export const HorizontalRiskBars = ({ rows }: { rows: { label: string; value: number; color: string }[] }) => (
  <Stack spacing={1.2}>
    {rows.map((row) => (
      <Box key={row.label}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: 13, color: '#333', fontWeight: 700 }}>{row.label}</Typography>
          <Typography sx={{ fontSize: 13, color: row.color, fontWeight: 800 }}>{row.value}%</Typography>
        </Stack>
        <Box sx={{ height: 10, bgcolor: '#edf2f5', borderRadius: '99px', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${row.value}%`, bgcolor: row.color, borderRadius: '99px' }} />
        </Box>
      </Box>
    ))}
  </Stack>
);

export const OrganSummaryGrid = ({ onSelect }: { onSelect?: (value: string) => void }) => (
  <Box sx={{ mx: 2, mt: 2, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
    {ORGAN_ITEMS.map((organ) => (
      <ButtonBase
        key={organ.value}
        onClick={() => onSelect?.(organ.value)}
        sx={{
          display: 'block',
          borderRadius: '14px',
          bgcolor: organ.risk === '나쁨' ? '#f5dcdd' : '#e8ecf1',
          boxShadow: organ.risk === '나쁨' ? 'inset 4px 4px 4px #daafb1' : '4px 4px 4px #c5d3da',
          p: 1.2,
          textAlign: 'center',
        }}
      >
        <Avatar alt="" src={organ.icon} variant="square" sx={{ width: 35, height: 35, mx: 'auto', borderRadius: 0, opacity: 0.8 }} />
        <Typography sx={{ mt: 0.7, color: '#333', fontSize: 13, fontWeight: 700 }}>{organ.label}</Typography>
        <Typography sx={{ mt: 0.2, color: organ.risk === '나쁨' ? '#e16569' : '#54a4cb', fontSize: 12, fontWeight: 800 }}>
          {organ.risk}
        </Typography>
      </ButtonBase>
    ))}
  </Box>
);

/* =========================================================================
 *  추가 공통 컴포넌트 (Figma 디자인 매칭용)
 * ========================================================================= */

/**
 * 연도 슬라이더 — 기본 범위는 현재(2026)부터 미래로.
 *  - 연도 라벨을 누르면 해당 연도로 점프 (정수 스냅)
 *  - 트랙·핸들을 드래그하면 연속값으로 변경 (드래그 중 morph 등 부드럽게 반영)
 * value 는 정수/소수 모두 허용 (예: 2026.4)
 */
export const YearSlider = ({
  years = [2026, 2027, 2028],
  labels,
  value,
  onChange,
  trackColor = '#6e8df0',
}: {
  years?: number[];
  labels?: string[]; // 지정 시 연도 숫자 대신 이 텍스트를 라벨로 표시
  value: number;
  onChange?: (v: number) => void;
  trackColor?: string;
}) => {
  const minYear = years[0];
  const maxYear = years[years.length - 1];
  const span = maxYear - minYear || 1;
  const clamped = Math.max(minYear, Math.min(maxYear, value));
  const pct = ((clamped - minYear) / span) * 100;

  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  // 드래그 중 최신 value 추적 (handleUp 스냅 계산용)
  const valueRef = useRef(value);
  valueRef.current = value;

  const yearFromClientX = (clientX: number) => {
    const track = trackRef.current;
    if (!track) return value;
    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return minYear + ratio * span;
  };

  // 가장 가까운 연도로 스냅
  const snapToNearestYear = (v: number) =>
    years.reduce((a, b) => (Math.abs(b - v) < Math.abs(a - v) ? b : a));

  const handleDown = (e: React.PointerEvent) => {
    if (!onChange) return;
    draggingRef.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    onChange(yearFromClientX(e.clientX));
  };
  const handleMove = (e: React.PointerEvent) => {
    if (!draggingRef.current || !onChange) return;
    onChange(yearFromClientX(e.clientX));
  };
  const handleUp = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    // 드래그를 놓으면 가장 가까운 연도 칸으로 핸들/모델이 정착
    onChange?.(snapToNearestYear(valueRef.current));
  };

  const activeYear = Math.round(clamped);

  return (
    <Box sx={{ width: '100%' }}>
      {/* 드래그 가능한 트랙 영역 (height 28) */}
      <Box
        ref={trackRef}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        sx={{
          position: 'relative',
          height: 28,
          cursor: onChange ? 'pointer' : 'default',
          touchAction: 'none',
        }}
      >
        {/* 트랙 바 (중심 y=19) */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 0,
            right: 0,
            height: 6,
            borderRadius: '99px',
            bgcolor: '#dfe5ee',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: trackColor, borderRadius: '99px' }} />
        </Box>

        {/* 세모 ▼ — 가로 중앙(left=pct%), 꼭지점이 원 위 끝(y=10)에 닿음 */}
        <Box
          sx={{
            position: 'absolute',
            left: `${pct}%`,
            top: 2,
            width: 0,
            height: 0,
            borderLeft: '7px solid transparent',
            borderRight: '7px solid transparent',
            borderTop: `8px solid ${trackColor}`,
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
          }}
        />

        {/* 핸들 원 — 중심 (pct%, y=19) */}
        <Box
          sx={{
            position: 'absolute',
            left: `${pct}%`,
            top: 19,
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: trackColor,
            border: '3px solid #fff',
            boxSizing: 'border-box',
            boxShadow: '0 2px 6px rgba(110,141,240,.4)',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
        />
      </Box>

      {/* 라벨 (클릭 시 해당 칸으로 스냅) — labels 있으면 텍스트, 없으면 연도 숫자 */}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.8, px: 0.2 }}>
        {years.map((y, i) => (
          <ButtonBase
            key={i}
            onClick={() => onChange?.(y)}
            sx={{
              color: y === activeYear ? '#333' : '#9aa6b2',
              fontSize: labels ? 12 : 13,
              fontWeight: y === activeYear ? 800 : 600,
              whiteSpace: 'nowrap',
            }}
          >
            {labels ? labels[i] : y}
          </ButtonBase>
        ))}
      </Stack>
    </Box>
  );
};

/**
 * 반원 게이지 차트 — 3색 구간(#37A9F8 / #F9B533 / #F95558) + 계기판 바늘.
 * 바늘은 게이지 중심에서 값 방향으로 향하며, 'status' 텍스트는 바늘 아래에 배치.
 */
export const SemicircleGauge = ({
  value = 50,
  status = '보통',
  size = 220,
}: {
  value?: number;
  status?: string;
  size?: number;
}) => {
  const w = size;
  const h = size * 0.66;       // 컨테이너 높이 (납작하게)
  const cx = w / 2;
  const cy = h * 0.82;         // 바늘 받침(피벗) 위치
  const r = w * 0.4;           // 호 반지름
  const stroke = size * 0.1;   // 호 두께 (크기 비례)
  const gap = 4;               // 구간 사이 여백 (도)

  // 각도: value 0~100 → 180°(왼쪽) ~ 360°(오른쪽), 위쪽 반원
  const toXY = (deg: number, radius: number) => {
    const a = (deg * Math.PI) / 180;
    return { x: cx + Math.cos(a) * radius, y: cy + Math.sin(a) * radius };
  };

  const arcPath = (startDeg: number, endDeg: number) => {
    const s = toXY(startDeg, r);
    const e = toXY(endDeg, r);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  // 3구간 경계 (180~360°를 33/33/34로 분할)
  const seg1 = 180 + 0.333 * 180;
  const seg2 = 180 + 0.666 * 180;

  // 바늘 각도
  const needleDeg = 180 + (Math.max(0, Math.min(100, value)) / 100) * 180;
  const nRad = (needleDeg * Math.PI) / 180;
  const dir = { x: Math.cos(nRad), y: Math.sin(nRad) };
  const perp = { x: -Math.sin(nRad), y: Math.cos(nRad) };
  const needleLen = r * 0.84;
  const baseW = 5.5;
  const tailLen = 15;
  const tip = { x: cx + dir.x * needleLen, y: cy + dir.y * needleLen };
  const bL = { x: cx + perp.x * baseW, y: cy + perp.y * baseW };
  const bR = { x: cx - perp.x * baseW, y: cy - perp.y * baseW };
  const tail = { x: cx - dir.x * tailLen, y: cy - dir.y * tailLen };

  return (
    <Box sx={{ position: 'relative', width: w, height: h + 14, mx: 'auto' }}>
      <svg width={w} height={h + 14}>
        {/* 3색 구간 호 — 끝을 각지게(butt), 구간 사이 gap 만큼 여백 */}
        <path d={arcPath(180, seg1 - gap)} fill="none" stroke="#37A9F8" strokeWidth={stroke} strokeLinecap="butt" />
        <path d={arcPath(seg1 + gap, seg2 - gap)} fill="none" stroke="#F9B533" strokeWidth={stroke} strokeLinecap="butt" />
        <path d={arcPath(seg2 + gap, 360)} fill="none" stroke="#F95558" strokeWidth={stroke} strokeLinecap="butt" />

        {/* 계기판 바늘 (마름모형) */}
        <polygon
          points={`${tip.x},${tip.y} ${bL.x},${bL.y} ${tail.x},${tail.y} ${bR.x},${bR.y}`}
          fill="#3a4a5c"
        />
        {/* 바늘 받침 */}
        <circle cx={cx} cy={cy} r={9} fill="#3a4a5c" />
        <circle cx={cx} cy={cy} r={4} fill="#fff" />
      </svg>

      {/* status 텍스트 — 바늘 받침 아래 */}
      {status && (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: cy + 16,
            transform: 'translateX(-50%)',
            px: 1.4,
            py: 0.3,
            borderRadius: '99px',
            border: '1.5px solid #38c172',
            bgcolor: '#fff',
            color: '#38c172',
            fontSize: 12,
            fontWeight: 800,
            whiteSpace: 'nowrap',
          }}
        >
          {status}
        </Box>
      )}
    </Box>
  );
};

// 가로 슬라이더 (위험도 / 등수 등)
export const PercentSlider = ({
  value,
  max = 100,
  color = '#54a4cb',
  trackBg = '#dfe5ee',
  showLabels,
}: {
  value: number;
  max?: number;
  color?: string;
  trackBg?: string;
  showLabels?: string[];
}) => {
  const pct = (value / max) * 100;
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ position: 'relative', height: 16, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', height: 6, bgcolor: trackBg, borderRadius: '99px' }} />
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            width: `${pct}%`,
            height: 6,
            bgcolor: color,
            borderRadius: '99px',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: `calc(${pct}% - 9px)`,
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: '#fff',
            border: `3px solid ${color}`,
            boxShadow: '0 2px 4px rgba(0,0,0,.08)',
          }}
        />
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.6, color: '#9aa6b2', fontSize: 11 }}>
        <Typography component="span" sx={{ fontSize: 11, color: '#9aa6b2' }}>1</Typography>
        {showLabels && (
          <Stack direction="row" spacing={3} sx={{ flex: 1, justifyContent: 'space-around' }}>
            {showLabels.map((l) => (
              <Typography key={l} component="span" sx={{ fontSize: 11, color: '#9aa6b2' }}>{l}</Typography>
            ))}
          </Stack>
        )}
        <Typography component="span" sx={{ fontSize: 11, color: '#9aa6b2' }}>{max}</Typography>
      </Stack>
    </Box>
  );
};

// 5각 레이더 차트 (비만, 면역, 이상지질혈증, 고혈압, 당뇨)
export const RadarChart = ({
  labels = ['비만', '면역', '이상지질협증', '고혈압', '당뇨'],
  values = [85, 60, 70, 75, 55],
  size = 280,
  color = '#7eb3d3',
}: {
  labels?: string[];
  values?: number[];
  size?: number;
  color?: string;
}) => {
  const cx = size / 2;
  const cy = size / 2;
  const maxR = size * 0.34;
  const n = labels.length;
  const angle = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const pt = (r: number, i: number) => ({
    x: cx + Math.cos(angle(i)) * r,
    y: cy + Math.sin(angle(i)) * r,
  });
  const grid = [0.25, 0.5, 0.75, 1];
  const dataPoints = values.map((v, i) => pt((v / 100) * maxR, i));
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ') + ' Z';

  return (
    <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto' }}>
      <svg width={size} height={size}>
        {/* 격자 5각형 */}
        {grid.map((g, gi) => {
          const path = Array.from({ length: n })
            .map((_, i) => {
              const p = pt(g * maxR, i);
              return `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`;
            })
            .join(' ') + ' Z';
          return <path key={gi} d={path} fill="none" stroke="#cbd4dc" strokeWidth={0.8} />;
        })}
        {/* 축 */}
        {Array.from({ length: n }).map((_, i) => {
          const p = pt(maxR, i);
          return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#cbd4dc" strokeWidth={0.8} />;
        })}
        {/* 데이터 영역 */}
        <path d={dataPath} fill={color} fillOpacity={0.35} stroke={color} strokeWidth={2} />
        {dataPoints.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={4} fill={color} stroke="#fff" strokeWidth={1.5} />
        ))}
      </svg>
      {/* 라벨 */}
      {labels.map((label, i) => {
        const p = pt(maxR + 16, i);
        return (
          <Typography
            key={label}
            sx={{
              position: 'absolute',
              left: p.x,
              top: p.y,
              transform: 'translate(-50%, -50%)',
              fontSize: 12,
              color: '#333',
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Typography>
        );
      })}
      {/* 중앙 하트 아이콘 */}
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#7eb3d3',
          fontSize: 22,
          fontWeight: 900,
        }}
      >
        ♡
      </Box>
    </Box>
  );
};

// 체중 변화 라인차트 (간단 SVG)
export const WeightLineChart = ({
  points = [40, 55, 65, 80, 75, 60, 50, 45],
  width = 360,
  height = 70,
  color = '#7ed4a3',
}: {
  points?: number[];
  width?: number;
  height?: number;
  color?: string;
}) => {
  const maxV = Math.max(...points);
  const stepX = width / (points.length - 1);
  const pathD = points
    .map((v, i) => {
      const x = i * stepX;
      const y = height - (v / maxV) * (height - 8) - 4;
      return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
    })
    .join(' ');
  const areaD =
    pathD + ` L ${width},${height} L 0,${height} Z`;
  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <path d={areaD} fill={color} fillOpacity={0.25} />
        <path d={pathD} fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round" />
      </svg>
    </Box>
  );
};

// 도넛/원형 작은 게이지 (위험도 70% 등)
export const RingGauge = ({
  value = 70,
  size = 90,
  color = '#7eb3d3',
}: {
  value?: number;
  size?: number;
  color?: string;
}) => {
  const r = size / 2 - 6;
  const c = 2 * Math.PI * r;
  const dash = (value / 100) * c;
  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e9eef5" strokeWidth={6} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={6}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
    </Box>
  );
};

/* =========================================================================
 *  위험도 / 등수 바 + 체중·레이더 차트 (화면 3·5 공용)
 * ========================================================================= */

// 위험도 값 → 구간 색
export const riskColorOf = (v: number) =>
  v <= 33 ? '#37A9F8' : v <= 66 ? '#F9B533' : '#F95558';

// 등수 구간 정의
export const RANK_SEGMENTS = [
  { name: '낮음', start: 0, end: 25, color: '#37A9F8' },
  { name: '주의', start: 25, end: 50, color: '#35AE17' },
  { name: '높음', start: 50, end: 75, color: '#F9B533' },
  { name: '매우높음', start: 75, end: 100, color: '#F9B533' },
];
const RANK_DOTS = [0, 25, 50, 75, 100];

export const rankSegOf = (v: number) =>
  RANK_SEGMENTS.find((s) => v >= s.start && v <= s.end) ?? RANK_SEGMENTS[0];

// 위험도 바 — 채워진 부분이 구간 색 (1~33 파랑 / 34~66 주황 / 67~100 빨강)
export const RiskBar = ({ value }: { value: number }) => {
  const v = Math.max(0, Math.min(100, value));
  const fillColor = riskColorOf(v);
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ position: 'relative', height: 18, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', height: 6, borderRadius: '99px', bgcolor: '#dfe5ee' }} />
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            width: `${v}%`,
            height: 6,
            borderRadius: '99px',
            bgcolor: fillColor,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: `${v}%`,
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: '#fff',
            border: `3px solid ${fillColor}`,
            boxSizing: 'border-box',
            boxShadow: '0 2px 4px rgba(0,0,0,.12)',
            transform: 'translateX(-50%)',
          }}
        />
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography component="span" sx={{ fontSize: 11, color: '#9aa6b2' }}>1</Typography>
        <Typography component="span" sx={{ fontSize: 11, color: '#9aa6b2' }}>100</Typography>
      </Stack>
    </Box>
  );
};

// 나의 등수 바 — 원 5개 + 현재 값 구간만 색 표시
export const RankBar = ({ value }: { value: number }) => {
  const v = Math.max(0, Math.min(100, value));
  const seg = rankSegOf(v);
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ position: 'relative', height: 18, display: 'flex', alignItems: 'center', mx: '9px' }}>
        <Box sx={{ width: '100%', height: 6, borderRadius: '99px', bgcolor: '#dfe5ee' }} />
        <Box
          sx={{
            position: 'absolute',
            left: `${seg.start}%`,
            width: `${seg.end - seg.start}%`,
            height: 6,
            borderRadius: '99px',
            bgcolor: seg.color,
          }}
        />
        {RANK_DOTS.map((p) => {
          const isActive = p === seg.start || p === seg.end;
          return (
            <Box
              key={p}
              sx={{
                position: 'absolute',
                left: `${p}%`,
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: isActive ? seg.color : '#fff',
                border: `1.5px solid ${isActive ? seg.color : '#c5d3da'}`,
                boxSizing: 'border-box',
                transform: 'translateX(-50%)',
              }}
            />
          );
        })}
        <Box
          sx={{
            position: 'absolute',
            left: `${v}%`,
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: '#fff',
            border: `3px solid ${seg.color}`,
            boxSizing: 'border-box',
            boxShadow: '0 2px 4px rgba(0,0,0,.12)',
            transform: 'translateX(-50%)',
          }}
        />
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography component="span" sx={{ fontSize: 11, color: '#9aa6b2' }}>1</Typography>
        <Typography component="span" sx={{ fontSize: 11, color: '#9aa6b2' }}>100</Typography>
      </Stack>
      <Stack direction="row" sx={{ mt: 0.3 }}>
        {RANK_SEGMENTS.map((s) => (
          <Typography
            key={s.name}
            component="span"
            sx={{
              flex: 1,
              textAlign: 'center',
              fontSize: 11,
              fontWeight: s.name === seg.name ? 800 : 600,
              color: s.name === seg.name ? s.color : '#9aa6b2',
            }}
          >
            {s.name}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

// 최근 체중 변화 카드 (MUI X LineChart) — 화면 3·5 공용
export const WeightChangeCard = () => (
  <SectionCard sx={{ bgcolor: '#fff' }}>
    <Typography sx={{ textAlign: 'center', color: '#333', fontSize: 14, fontWeight: 700, mb: 1.5 }}>
      최근 체중 변화
    </Typography>
    <Stack direction="row" spacing={4} justifyContent="center" sx={{ mb: 1 }}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ color: '#333', fontSize: 22, fontWeight: 900 }}>
          2.3<Box component="span" sx={{ fontSize: 12 }}>kg</Box>
        </Typography>
        <Typography sx={{ color: '#7ed4a3', fontSize: 11, mt: 0.3 }}>▼ 진행 (1. 2.)</Typography>
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ color: '#333', fontSize: 22, fontWeight: 900 }}>
          0.6<Box component="span" sx={{ fontSize: 12 }}>kg</Box>
        </Typography>
        <Typography sx={{ color: '#ff8000', fontSize: 11, mt: 0.3 }}>▲ 7일 전에 비해 (1. 9.)</Typography>
      </Box>
    </Stack>
    <Box sx={{ width: '100%', height: 130 }}>
      <LineChart
        height={130}
        margin={{ top: 10, bottom: 24, left: 10, right: 10 }}
        series={[
          {
            data: [90.5, 91.2, 89.4, 92.8, 88.1, 90.0, 89.6, 88.2],
            area: true,
            color: '#7ed4a3',
            showMark: true,
            curve: 'catmullRom',
          },
        ]}
        xAxis={[
          {
            data: ['1.2', '1.3', '1.4', '1.5', '1.6', '1.7', '1.8', '1.9'],
            scaleType: 'point',
            disableLine: true,
            disableTicks: true,
          },
        ]}
        yAxis={[{ disableLine: true, disableTicks: true }]}
        leftAxis={null}
        sx={{
          '& .MuiAreaElement-root': { fillOpacity: 0.22 },
          '& .MuiChartsAxis-tickLabel': { fill: '#9aa6b2', fontSize: 10 },
          '& .MuiMarkElement-root': { stroke: '#7ed4a3', fill: '#fff', strokeWidth: 2, r: 3 },
        }}
      />
    </Box>
  </SectionCard>
);

// 5각 레이더 카드 (Chart.js Radar) — 화면 3·5 공용
export const HealthRadarCard = () => (
  <SectionCard sx={{ bgcolor: '#fff', py: 3 }}>
    <Box sx={{ width: '100%', height: 260 }}>
      <Radar
        data={{
          labels: ['비만', '면역', '이상지질협증', '고혈압', '당뇨'],
          datasets: [
            {
              data: [85, 65, 75, 80, 50],
              backgroundColor: 'rgba(126, 179, 211, 0.4)',
              borderColor: '#5f9ec6',
              borderWidth: 2,
              pointBackgroundColor: '#5f9ec6',
              pointBorderColor: '#fff',
              pointRadius: 3,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            r: {
              min: 0,
              max: 100,
              ticks: { display: false, stepSize: 25 },
              grid: { color: '#dbe2e9' },
              angleLines: { color: '#dbe2e9' },
              pointLabels: {
                color: '#333',
                font: { size: 14, weight: 700 },
              },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
        }}
      />
    </Box>
  </SectionCard>
);
